import { Music2 } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"

/**
 * OKS-509 — In-feed TikTok player (TRD KD-1: CLIENT-SIDE embed only, NO server
 * oEmbed call). Renders TikTok's first-party iframe player
 * (`https://www.tiktok.com/embed/v2/{videoId}`) at the 366px / 9:16 reference.
 *
 * Graceful degradation (UX Spec §3 "embed fails → fallback card"):
 *   - A thumbnail poster (or a branded glyph placeholder when `thumbnailUrl`
 *     is null) sits BEHIND the iframe and a shimmer skeleton sits ON TOP until
 *     the iframe paints (`onLoad`). So even if the iframe never loads
 *     (network blocked / private video / test env), the viewer still sees the
 *     poster — and the ❤ deep-link in the action row always works.
 *   - `onError` flips to an explicit fallback (poster + glyph), no broken frame.
 *
 * Why a plain iframe vs TikTok's `embed.js`: the script mutates the DOM out of
 * band and has no clean re-scan API for a React list — an iframe is
 * self-contained, lazy-loadable, and never re-mounts when the feed re-renders
 * after a cheer (keyed by post id upstream), so the player doesn't reload.
 *
 * NO network call is made server-side or in tests; jsdom/Playwright simply
 * never paint the cross-origin frame, so the poster + skeleton render and the
 * "boundary mocked" rule holds by construction.
 *
 * Locked testids: `tiktok-embed` (container), `tiktok-embed-iframe`,
 * `tiktok-embed-fallback` (poster/glyph layer).
 */
interface TiktokEmbedProps {
  videoId: string
  /** TikTok thumbnail; null → branded glyph placeholder. */
  thumbnailUrl: string | null
  /** Accessible caption for the player + poster alt. */
  caption: string
  className?: string
}

/** TikTok's first-party iframe player endpoint. Client-side only. */
function embedSrc(videoId: string): string {
  return `https://www.tiktok.com/embed/v2/${encodeURIComponent(videoId)}`
}

export function TiktokEmbed({
  videoId,
  thumbnailUrl,
  caption,
  className,
}: TiktokEmbedProps) {
  const { t } = useTranslation()
  const [loaded, setLoaded] = useState(false)
  const [failed, setFailed] = useState(false)

  return (
    <div
      data-testid="tiktok-embed"
      data-loaded={loaded}
      data-failed={failed}
      className={cn(
        "relative mx-auto aspect-[9/16] w-full max-w-[366px] overflow-hidden rounded-xl bg-neutral-900",
        className,
      )}
    >
      {/* Poster / fallback layer — always rendered behind the iframe. */}
      <div
        data-testid="tiktok-embed-fallback"
        aria-hidden={loaded ? "true" : undefined}
        className="absolute inset-0 flex items-center justify-center"
      >
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt={caption || t("crew_feed.post.embed_alt")}
            className="size-full object-cover"
            loading="lazy"
          />
        ) : (
          <Music2
            aria-hidden="true"
            className="size-10 text-neutral-600"
            data-testid="tiktok-embed-glyph"
          />
        )}
      </div>

      {/* Loading shimmer until the iframe paints (hidden once loaded/failed). */}
      {!loaded && !failed && (
        <Skeleton
          data-testid="tiktok-embed-skeleton"
          className="absolute inset-0 rounded-none bg-neutral-800/60"
        />
      )}

      {/* The first-party TikTok player. Lazy + sandboxed; covers the poster
          when it paints. On error we keep the poster (no broken frame). */}
      {!failed && (
        <iframe
          data-testid="tiktok-embed-iframe"
          src={embedSrc(videoId)}
          title={caption || t("crew_feed.post.embed_alt")}
          loading="lazy"
          allow="encrypted-media; fullscreen"
          className={cn(
            "absolute inset-0 size-full border-0 transition-opacity duration-300",
            loaded ? "opacity-100" : "opacity-0",
          )}
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
        />
      )}
    </div>
  )
}
